<template>
  <div class="local-menu">
    <div class="menu-title">{{ title }}</div>
    <div class="menu-content">
      <LocalMenuItem
        v-for="menu in visibleMenuList"
        :key="menu.path"
        :menu="menu"
        :level="0"
        :selected-keys="selectedKeys"
        :open-keys="openKeys"
        :closing-keys="closingKeys"
        :full-menu-list="props.menuList"
        @toggle-sub-menu="toggleSubMenu"
        @menu-click="handleMenuClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LocalMenuItem from './LocalMenuItem.vue'

  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    menuList: {
      type: Array,
      required: true
    },
    activeMenu: {
      type: String,
      default: ''
    }
  })

  const route = useRoute()
const router = useRouter()

// 只显示 visible 不为 1 的菜单项
const visibleMenuList = computed(() => {
  return props.menuList.filter(menu => Number(menu?.meta?.visible) !== 1)
})

// 菜单状态管理
const selectedKeys = ref([])
const openKeys = ref([])
const closingKeys = ref([])

// 跟踪最后一次手动操作的菜单路径（用于区分路由变化和用户点击）
const lastManualActionPath = ref('')

  // 计算当前活跃的菜单路径
  const currentActiveMenu = computed(() => {
    return props.activeMenu || route.path
  })

  // 处理菜单点击事件，实现路由跳转
  const handleMenuClick = (key) => {
    if (key !== route.path) {
      router.push(key)
    }
  }

// 切换子菜单展开/收起状态
const toggleSubMenu = (path) => {
  // 记录最后一次手动操作的路径
  lastManualActionPath.value = path
  
  const index = openKeys.value.indexOf(path)
  if (index > -1) {
    // 如果菜单已经展开，则收起它（只收起当前菜单，不影响其他菜单）
    closingKeys.value.push(path)
    setTimeout(() => {
      openKeys.value.splice(index, 1)
      closingKeys.value.splice(closingKeys.value.indexOf(path), 1)
    }, 300)
  } else {
    // 如果菜单未展开，则展开它
    // 对于顶级菜单：关闭其他顶级菜单，保留当前菜单的父级
    // 对于非顶级菜单：只展开当前菜单，不影响其他菜单
    
    const currentPathLevel = path.split('/').length
    
    if (currentPathLevel === 2) {
      // 点击的是顶级菜单
      const otherTopLevelKeys = openKeys.value.filter(key => {
        const keyLevel = key.split('/').length
        return keyLevel === 2 && key !== path
      })
      
      // 关闭其他顶级菜单
      otherTopLevelKeys.forEach(key => {
        const keyIndex = openKeys.value.indexOf(key)
        if (keyIndex > -1) {
          closingKeys.value.push(key)
          setTimeout(() => {
            openKeys.value.splice(keyIndex, 1)
            closingKeys.value.splice(closingKeys.value.indexOf(key), 1)
          }, 300)
        }
      })
    }
    
    // 展开当前菜单
    openKeys.value.push(path)
  }
}

  // 检查子菜单是否展开
  const isOpen = (path) => openKeys.value.includes(path)

  // 检查路径是否是当前选中路径的父级
  const isParentOfActivePath = (parentPath) => {
    const currentPath = currentActiveMenu.value
    return currentPath.startsWith(parentPath) &&
      (currentPath.length === parentPath.length || currentPath[ parentPath.length ] === '/')
  }

// 监听路由变化，更新选中的菜单项并自动展开父级菜单
watch(() => currentActiveMenu.value, (newPath) => {
  // 只有当路由变化不是由当前手动操作引起时，才自动展开父级菜单
  if (lastManualActionPath.value !== newPath) {
    
    // 自动展开所有父级菜单（基于路径分割和菜单树关系）
    const expandParentMenus = (path) => {
      // 方法1：通过路径分割找到所有父级路径
      const pathBasedParents = []
      let currentPath = path
      
      while (currentPath.includes('/')) {
        const lastSlashIndex = currentPath.lastIndexOf('/')
        if (lastSlashIndex > 0) {
          currentPath = currentPath.substring(0, lastSlashIndex)
          pathBasedParents.push(currentPath)
        } else {
          break
        }
      }
      
      // 方法2：通过菜单树关系找到所有逻辑父级（解决三级目录中间层级问题）
      const menuTreeBasedParents = []
      
      // 递归查找菜单树中的所有父级（包括隐藏的菜单项）
      const findParentsInMenuTree = (menuList, targetPath, currentParents = []) => {
        for (const menu of menuList) {
          if (menu.path === targetPath) {
            return [...currentParents]
          }
          
          if (menu.children && menu.children.length > 0) {
            const found = findParentsInMenuTree(menu.children, targetPath, [...currentParents, menu.path])
            if (found) return found
          }
        }
        return null
      }
      
      const menuParents = findParentsInMenuTree(props.menuList, path) || []
      
      // 合并两种方法的结果（去重）
      const allParentPaths = [...new Set([...pathBasedParents, ...menuParents])]
      
      // 设置选中状态：包括当前路径和所有父级路径
      selectedKeys.value = [path, ...allParentPaths]
      
      // 展开所有父级菜单
      allParentPaths.forEach(parentPath => {
        if (!openKeys.value.includes(parentPath)) {
          openKeys.value.push(parentPath)
        }
      })
    }
    
    expandParentMenus(newPath)
    
    // 下次DOM更新后自动滚动到高亮项
    nextTick(() => {
      scrollToActiveMenuItem()
    })
  }
  
  // 重置手动操作跟踪
  lastManualActionPath.value = ''
}, { immediate: true })

// 自动滚动到高亮的菜单项
const scrollToActiveMenuItem = () => {
  const menuContainer = document.querySelector('.local-menu')
  if (!menuContainer) return
  
  // 查找高亮的菜单项
  const activeItems = menuContainer.querySelectorAll('.active')
  if (activeItems.length === 0) return
  
  // 获取最后一个高亮项（最深层级）
  const lastActiveItem = activeItems[activeItems.length - 1]
  
  // 计算滚动位置，确保高亮项在可见区域
  const itemRect = lastActiveItem.getBoundingClientRect()
  const containerRect = menuContainer.getBoundingClientRect()
  
  // 如果高亮项不在可见区域内，则滚动到该位置
  if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
    lastActiveItem.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    })
  }
}
</script>

<style lang="scss" scoped>
  .local-menu {
    width: 220px;
    height: 100%;
    background-color: #fff;
    overflow-y: auto;
    font-weight: 500;

    .menu-title {
      font-size: 20px;
      font-weight: 500;
      margin: 0 0 12px 28px;
      padding-top: 32px;
    }

    .menu-content {
      padding: 0 16px;
    }

    .menu-item {
      padding: 12px 16px;
      width: 188px;
      min-height: 48px;
      cursor: pointer;
      border-radius: 8px;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-black) !important;
      white-space: normal;
      word-break: break-all;

      &:hover {
        background: #F7F7F7 !important;
      }

      &.active {
        background: rgba(70, 95, 253, 0.1) !important;
        color: var(--color-primary) !important;
      }
    }

    .sub-menu-item {
      .sub-menu-title {
        padding: 12px 16px;
        width: 188px;
        min-height: 48px;
        cursor: pointer;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        color: var(--color-black) !important;

        &:hover {
          background: #f5f5f5 !important;
        }

        &.active {
          // background: transparent !important; /* 有子级选中时目录无背景色 */
          color: var(--color-primary) !important;
        }

        .menu-text {
          flex: 1;
          white-space: normal;
          word-break: break-all;
          padding-right: 8px;
        }

        .arrow-icon {
          width: 8px;
          height: 5.333px;
          transition: transform 0.3s ease;
          color: var(--color-black) !important;

          &.active {
            color: var(--color-primary) !important;
          }
        }
      }

      .sub-menu-children {
        padding-left: 0;
        animation: slideDown 0.3s ease;

        .menu-item {
          padding: 12px 16px 12px 24px;
          font-weight: 400;
        }

        &.closing {
          animation: slideUp 0.3s ease;
        }
      }
    }

    // 滚动条样式
    &::-webkit-scrollbar {
      width: 8px;

      &-track {
        background: #f1f1f1;
        border-radius: 4px;
      }

      &-thumb {
        background: #c1c1c1;
        border-radius: 4px;

        &:hover {
          background: #a8a8a8;
        }
      }
    }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }

    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
</style>